import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, Stack, Switch, TextField, Typography, useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatSidebar from "../components/ChatSidebar";

const DAY = 24 * 60 * 60 * 1000;

const getToken = () => {
  const token = localStorage.getItem("jwtToken") || localStorage.getItem("authToken") || sessionStorage.getItem("token") || "";
  return token.startsWith("Bearer ") ? token.slice(7) : token;
};

const messageText = (message) => {
  const value = message?.texto ?? message?.content ?? message?.message ?? "";
  return typeof value === "string" ? value : value ? JSON.stringify(value) : "";
};

const messageDate = (message) => message?.dataHoraCadastroAtualizado ?? message?.dataHoraCadastro ?? message?.createdAt ?? message?.dataHora;

const saoPauloTimestamp = (value) => {
  if (!value) return Number.NaN;
  const normalized = String(value).replace(" ", "T");
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  return new Date(hasTimezone ? normalized : `${normalized}-03:00`).getTime();
};

const friendlyError = (error, fallback) => {
  if (!error?.response) {
    return "A API nao respondeu a esta solicitacao. Verifique a conexao com o servidor.";
  }
  const backendMessage = error.response.data?.mensagem || error.response.data?.message;
  const status = error?.response?.status;
  if (status === 403) return "Voce nao tem permissao para realizar esta acao.";
  if (status === 409) return "Esta conversa foi alterada por outro usuario. Atualize e tente novamente.";
  if (status === 422) return backendMessage || "O envio foi bloqueado pelas regras do WhatsApp.";
  if (status === 502) return "Nao foi possivel enviar pelo WhatsApp neste momento. Tente novamente.";
  return backendMessage || fallback;
};

const templateBody = (template) => template?.components?.find((item) => String(item?.type).toUpperCase() === "BODY");
const templateVariables = (template) => [...new Set([...String(templateBody(template)?.text || "").matchAll(/{{\s*(\d+)\s*}}/g)].map((match) => match[1]))];
const templateText = (template, values) => String(templateBody(template)?.text || template?.name || "").replace(/{{\s*(\d+)\s*}}/g, (_, key) => values[key] || "");
const templatePreviewText = (template, values) => String(templateBody(template)?.text || "").replace(/{{\s*(\d+)\s*}}/g, (_, key) => values[key] || `{{${key}}}`);

const formatDate = (value) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "";
  return new Date(value).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const TemplateMessagePreview = ({ template, values }) => (
  <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: "100%" }}>
    <Box sx={{ px: 1.25, py: 1, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #d6dadd" }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#25d366", display: "grid", placeItems: "center", color: "#fff" }}><WhatsAppIcon sx={{ fontSize: 17 }} /></Box>
      <Box><Typography sx={{ color: "#111b21", fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>WhatsApp</Typography><Typography sx={{ color: "#667781", fontSize: 10, lineHeight: 1.2 }}>Prévia da mensagem</Typography></Box>
    </Box>
    <Box sx={{ bgcolor: "#efeae2", minHeight: 235, p: 1.5, display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
      <Paper elevation={0} sx={{ p: 1.25, borderRadius: "8px 0 8px 8px", bgcolor: "#d9fdd3", color: "#111b21", maxWidth: "94%", boxShadow: "0 1px 1px rgba(0,0,0,0.08)" }}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.45 }}>{template ? templatePreviewText(template, values) : "Selecione um template para visualizar a mensagem."}</Typography>
        <Typography sx={{ mt: 0.5, color: "#667781", textAlign: "right", fontSize: 9 }}>10:42</Typography>
      </Paper>
    </Box>
  </Paper>
);

export default function AiDirectPreVenda({
  setForceTokenExpired,
  productOrigin = "PREV",
  productLabel = "Pre-venda",
  historyEndpoint = "/produto/sdr/lista-mensagens",
  historyClientParam = "idClienteAipim",
  historyWhatsAppParam = "whatsAppId",
  referenceLabel = "lead",
}) {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const chatColors = isDarkMode ? {
    background: "#0b141a", header: "#202c33", divider: "#31424c", status: "#173b3a",
    text: "#fff", secondary: "#b0bec5", icon: "#d5dde1", user: "#202c33",
    assistant: "#005c4b", direct: "#0b6b60", directBorder: "#4db6ac",
    userRole: "#ffca28", assistantRole: "#42a5f5", directRole: "#80cbc4",
    pattern: "radial-gradient(circle at 16px 16px, rgba(255,255,255,0.035) 1px, transparent 1px)",
    inputBorder: "#607d8b",
  } : {
    background: "#efeae2", header: "#f0f2f5", divider: theme.palette.divider, status: "#e0f2f1",
    text: theme.palette.text.primary, secondary: theme.palette.text.secondary, icon: theme.palette.text.secondary, user: "#fff",
    assistant: "#d9fdd3", direct: "#cfe8ff", directBorder: "#1976d2",
    userRole: "#ed6c02", assistantRole: "#1976d2", directRole: "#1565c0",
    pattern: "radial-gradient(circle at 16px 16px, rgba(0,0,0,0.035) 1px, transparent 1px)",
    inputBorder: theme.palette.divider,
  };
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [activeAttendancesByLead, setActiveAttendancesByLead] = useState({});
  const [onlyActive, setOnlyActive] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [assumeDialogOpen, setAssumeDialogOpen] = useState(false);
  const [assumeError, setAssumeError] = useState("");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateValues, setTemplateValues] = useState({});
  const [firstMessage, setFirstMessage] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const isLoadingConversationRef = useRef(false);

  const auth = useMemo(() => {
    try {
      const token = getToken();
      const decoded = jwtDecode(token);
      return {
        token,
        idCliente: decoded?.id_cliente ?? decoded?.idCliente ?? decoded?.idClienteAipim ?? null,
        idUsuario: Number(decoded?.id_usuario ?? decoded?.idUsuario ?? 0) || null,
        role: String(decoded?.role || "").toLowerCase(),
        readonly: decoded?.readonly === true,
      };
    } catch {
      return { token: "", idCliente: null, idUsuario: null, role: "", readonly: true };
    }
  }, []);
  const requestConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${auth.token}` } }), [auth.token]);

  const loadLeads = useCallback(async () => {
    if (!auth.token) return;
    setLoadingLeads(true);
    try {
      const response = await axios.get(`${baseURL}/aidirect/lista-contatos`, {
        ...requestConfig,
        params: { produtoOrigem: productOrigin, ativo: onlyActive ? true : undefined },
      });
      const contacts = (response.data?.data || []).map((contact) => ({
        ...contact,
        // ChatSidebar and the handoff actions use the lead id as their stable reference.
        id: contact.idReferencia,
      }));
      const activeByLead = contacts.reduce((result, contact) => {
        if (contact.atendimento?.ativo) result[contact.id] = contact.atendimento;
        return result;
      }, {});
      setActiveAttendancesByLead(activeByLead);
      setLeads(contacts);
    } catch (error) {
      if (error?.response?.status === 401) setForceTokenExpired?.(true);
      setFeedback({ severity: "error", message: `Nao foi possivel carregar as conversas de ${productLabel}.` });
    } finally {
      setLoadingLeads(false);
    }
  }, [auth.token, baseURL, onlyActive, productLabel, productOrigin, requestConfig, setForceTokenExpired]);

  const loadConversation = useCallback(async (lead, { silent = false, includeAttendance = true } = {}) => {
    if (!lead || !auth.token || !auth.idCliente || isLoadingConversationRef.current) return;
    isLoadingConversationRef.current = true;
    if (!silent) {
      setLoadingConversation(true);
      setAttendance(lead.atendimento || null);
    }
    try {
      const requests = [axios.get(`${baseURL}${historyEndpoint}`, { ...requestConfig, params: { [historyClientParam]: auth.idCliente, [historyWhatsAppParam]: lead.whatsAppId } })];
      if (includeAttendance) requests.push(axios.get(`${baseURL}/aidirect/atendimentos`, { ...requestConfig, params: { ativo: true, produtoOrigem: productOrigin, idReferencia: lead.id } }));
      const [history, active] = await Promise.all(requests);
      setMessages(Array.isArray(history.data) ? history.data : []);
      if (active) setAttendance((Array.isArray(active.data) ? active.data : [])[0] || null);
    } catch (error) {
      if (error?.response?.status === 401) setForceTokenExpired?.(true);
      if (!silent) setFeedback({ severity: "error", message: "Nao foi possivel atualizar esta conversa." });
      if (!silent) {
        setMessages([]);
        setAttendance(null);
      }
    } finally {
      if (!silent) setLoadingConversation(false);
      isLoadingConversationRef.current = false;
    }
  }, [auth.idCliente, auth.token, baseURL, historyClientParam, historyEndpoint, historyWhatsAppParam, productOrigin, requestConfig, setForceTokenExpired]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadConversation(selectedLead); }, [loadConversation, selectedLead]);
  useEffect(() => {
    if (!selectedLead) return undefined;
    const intervalId = window.setInterval(() => {
      if (!document.hidden) loadConversation(selectedLead, { silent: true, includeAttendance: false });
    }, 20 * 1000);
    return () => window.clearInterval(intervalId);
  }, [loadConversation, selectedLead]);

  const lastUserMessage = useMemo(() => [...messages].reverse().find((message) => message?.role === "user"), [messages]);
  const withinWindow = useMemo(() => {
    const date = messageDate(lastUserMessage);
    const timestamp = saoPauloTimestamp(date);
    return Number.isFinite(timestamp) && Date.now() - timestamp < DAY;
  }, [lastUserMessage]);
  const selectedTemplate = useMemo(() => templates.find((template) => String(template.id) === selectedTemplateId) || null, [selectedTemplateId, templates]);
  const isResponsible = attendance && Number(attendance.idUsuarioResponsavel) === auth.idUsuario;
  const canSend = Boolean(attendance) && !auth.readonly && isResponsible;
  const canSendText = canSend && withinWindow;
  const canClose = Boolean(attendance) && !auth.readonly && (isResponsible || ["admin", "gestor"].includes(auth.role));

  const isAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= 24;
  }, []);

  useEffect(() => {
    shouldStickToBottomRef.current = true;
  }, [selectedLead]);

  useEffect(() => {
    if (!shouldStickToBottomRef.current) return;
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ block: "end" }));
  }, [messages, loadingConversation]);

  const loadApprovedTemplates = async () => {
    if (templates.length) return true;
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${baseURL}/aidirect/templates`, { ...requestConfig, params: { produtoOrigem: productOrigin, limit: 50 } });
      setTemplates((response.data?.data || []).filter((template) => template.status === "APPROVED"));
      return true;
    } catch (error) {
      setFeedback({ severity: "error", message: friendlyError(error, "Nao foi possivel carregar os templates aprovados.") });
      return false;
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openAssume = async () => {
    if (!selectedLead || auth.readonly) return;
    setFeedback(null);
    setAssumeError("");
    setAssumeDialogOpen(true);
    if (!withinWindow) await loadApprovedTemplates();
  };

  const openTemplateDialog = async () => {
    if (!canSend) return;
    setFeedback(null);
    setTemplateDialogOpen(true);
    await loadApprovedTemplates();
  };

  const resetAssume = (force = false) => {
    if (submitting && !force) return;
    setAssumeDialogOpen(false);
    setAssumeError("");
    setFirstMessage("");
    setSelectedTemplateId("");
    setTemplateValues({});
  };

  const assumeAndSend = async () => {
    const text = withinWindow ? firstMessage.trim() : templateText(selectedTemplate, templateValues).trim();
    if (!selectedLead || !text || (!withinWindow && !selectedTemplate)) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const payload = withinWindow
        ? {
          produtoOrigem: productOrigin,
          idReferencia: selectedLead.id,
          tipoMensagem: "text",
          texto: text,
        }
        : (() => {
        const variables = templateVariables(selectedTemplate);
        return {
          produtoOrigem: productOrigin,
          idReferencia: selectedLead.id,
          tipoMensagem: "template",
          nome: selectedTemplate.name,
          idioma: selectedTemplate.language,
          texto: text,
          componentes: variables.length ? [{ type: "body", parameters: variables.map((key) => ({ type: "text", text: templateValues[key] || "" })) }] : [],
        };
      })();
      await axios.post(`${baseURL}/aidirect/atendimentos/assumir-enviar`, payload, requestConfig);
      resetAssume(true);
    } catch (error) {
      setAssumeError(friendlyError(error, "Nao foi possivel assumir esta conversa."));
    } finally {
      setSubmitting(false);
      await loadConversation(selectedLead);
      await loadLeads();
    }
  };

  const sendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !attendance || !canSendText) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await axios.post(`${baseURL}/aidirect/mensagens`, { idAtendimento: attendance.idExterno, texto: text }, requestConfig);
      setNewMessage("");
    } catch (error) {
      setFeedback({ severity: "error", message: friendlyError(error, "Nao foi possivel enviar a mensagem.") });
    } finally {
      setSubmitting(false);
      await loadConversation(selectedLead);
    }
  };

  const sendTemplate = async () => {
    const text = templateText(selectedTemplate, templateValues).trim();
    if (!attendance || !canSend || !selectedTemplate || !text) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const variables = templateVariables(selectedTemplate);
      await axios.post(`${baseURL}/aidirect/templates/enviar`, {
        idAtendimento: attendance.idExterno,
        nome: selectedTemplate.name,
        idioma: selectedTemplate.language,
        texto: text,
        componentes: variables.length ? [{ type: "body", parameters: variables.map((key) => ({ type: "text", text: templateValues[key] || "" })) }] : [],
      }, requestConfig);
      setTemplateDialogOpen(false);
      setSelectedTemplateId("");
      setTemplateValues({});
    } catch (error) {
      setFeedback({ severity: "error", message: friendlyError(error, "Nao foi possivel enviar o template.") });
    } finally {
      setSubmitting(false);
      await loadConversation(selectedLead);
    }
  };

  const returnToAi = async () => {
    if (!attendance || !canClose) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await axios.patch(`${baseURL}/aidirect/atendimentos/encerrar`, { idAtendimento: attendance.idExterno }, requestConfig);
      await loadConversation(selectedLead);
      await loadLeads();
    } catch (error) {
      setFeedback({ severity: "error", message: friendlyError(error, "Nao foi possivel devolver esta conversa para a IA.") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "min(760px, calc(100vh - 190px))", minHeight: 520, overflow: "hidden", border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", isolation: "isolate" }}>
      {loadingLeads ? <Box sx={{ width: { xs: "100%", md: 340 }, display: "grid", placeItems: "center", borderRight: { md: "1px solid" }, borderColor: "divider" }}><CircularProgress /></Box> : <ChatSidebar contacts={leads} selectedContact={selectedLead} onSelectContact={setSelectedLead} currentTab="aidirect" renderContactMeta={(lead) => activeAttendancesByLead[lead.id] ? <Chip size="small" color="success" label="Ativa" /> : null} headerAction={<Stack direction="row" spacing={0.25} alignItems="center"><Switch size="small" checked={onlyActive} onChange={(event) => { setSelectedLead(null); setAttendance(null); setMessages([]); setOnlyActive(event.target.checked); }} inputProps={{ "aria-label": "Filtrar atendimentos aiDirect ativos" }} /><Typography variant="caption" fontWeight={700}>aiDirect</Typography></Stack>} />}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: chatColors.background, overflow: "hidden" }}>
        {!selectedLead ? <Box sx={{ display: "grid", placeItems: "center", flex: 1 }}><Typography color="text.secondary">Selecione uma conversa para iniciar.</Typography></Box> : <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 2, py: 1.25, bgcolor: chatColors.header, borderBottom: "1px solid", borderColor: chatColors.divider, color: chatColors.text }}><Box minWidth={0}><Typography fontWeight={700} noWrap>{selectedLead.nome || "Sem nome"}</Typography><Typography variant="caption" sx={{ color: chatColors.secondary }}>{selectedLead.whatsAppId}</Typography></Box><Stack direction="row" spacing={1} alignItems="center">{attendance ? <Chip size="small" color="success" label="Atendimento ativo" /> : <Button variant="contained" size="small" onClick={openAssume} disabled={auth.readonly}>Assumir conversa</Button>}<IconButton aria-label="Atualizar mensagens" onClick={() => loadConversation(selectedLead, { silent: true, includeAttendance: false })} sx={{ color: chatColors.icon }}><RefreshIcon /></IconButton></Stack></Stack>
          {attendance && <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ px: 2, py: 1, bgcolor: chatColors.status, borderBottom: "1px solid", borderColor: chatColors.divider, color: chatColors.text }}><Typography variant="body2">Sob responsabilidade humana: <strong>{attendance.nomeUsuarioResponsavel || `Usuario #${attendance.idUsuarioResponsavel}`}</strong></Typography>{canClose && <Button size="small" variant="outlined" color="inherit" onClick={returnToAi} disabled={submitting} sx={{ borderColor: chatColors.text, color: chatColors.text, "&:hover": { borderColor: chatColors.text, bgcolor: "action.hover" } }}>Devolver para IA</Button>}</Stack>}
          {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ m: 1 }}>{feedback.message}</Alert>}
          <Box ref={messagesContainerRef} onScroll={() => { shouldStickToBottomRef.current = isAtBottom(); }} sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", gap: 1, bgcolor: chatColors.background, backgroundImage: chatColors.pattern, backgroundSize: "32px 32px" }}>{loadingConversation ? <Box sx={{ display: "grid", placeItems: "center", flex: 1 }}><CircularProgress /></Box> : messages.map((message, index) => { const user = message.role === "user"; const direct = message.role === "aidirect"; return <Paper key={`${message.id || index}-${messageDate(message) || index}`} sx={{ alignSelf: user ? "flex-start" : "flex-end", maxWidth: { xs: "88%", md: "72%" }, minWidth: { xs: 180, md: 220 }, p: 1.5, bgcolor: user ? chatColors.user : direct ? chatColors.direct : chatColors.assistant, color: chatColors.text, border: direct ? `1px solid ${chatColors.directBorder}` : "none", borderRadius: "10px", position: "relative", overflowWrap: "anywhere", boxShadow: "0 1px 2px rgba(0,0,0,0.16)" }}><Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.5, color: user ? chatColors.userRole : direct ? chatColors.directRole : chatColors.assistantRole }}>{user ? selectedLead.nome : direct ? "Humano via aiDirect" : "Agente"}</Typography><Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: chatColors.text, mb: 1.5, lineHeight: 1.45 }}>{messageText(message)}</Typography><Typography variant="caption" sx={{ position: "absolute", bottom: 4, right: 6, color: chatColors.secondary }}>{formatDate(messageDate(message))}</Typography></Paper>; })}<Box ref={messagesEndRef} /></Box>
          <Stack direction="row" spacing={1} sx={{ p: 1.25, bgcolor: chatColors.header, borderTop: "1px solid", borderColor: chatColors.divider }}>{canSend && !withinWindow ? <Button fullWidth variant="contained" onClick={openTemplateDialog} disabled={submitting}>Enviar template</Button> : <><TextField fullWidth size="small" placeholder={canSend ? "Digite uma mensagem" : attendance ? "Apenas o responsavel pode enviar mensagens" : "Assuma a conversa para responder"} value={newMessage} onChange={(event) => setNewMessage(event.target.value)} disabled={!canSendText || submitting} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} sx={{ "& .MuiOutlinedInput-root": { color: chatColors.text, "& fieldset": { borderColor: chatColors.inputBorder } }, "& .MuiInputBase-input::placeholder": { color: chatColors.secondary, opacity: 1 } }} /><IconButton color="primary" aria-label="Enviar mensagem" onClick={sendMessage} disabled={!canSendText || !newMessage.trim() || submitting}><SendIcon /></IconButton></>}</Stack>
        </>}
      </Box>
      <Dialog open={assumeDialogOpen} onClose={resetAssume} fullWidth maxWidth="md">
        <DialogTitle>Assumir conversa</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 0.5 }}>Ao assumir, a IA deixa de responder temporariamente e a conversa passa a ficar sob sua responsabilidade.</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Contato: <strong>{selectedLead?.nome || "Sem nome"}</strong></Typography>
            <Typography variant="caption" color="text.secondary">WhatsApp: <strong>{selectedLead?.whatsAppId || "nao informado"}</strong></Typography>
            <Typography variant="caption" color="text.secondary">Referencia do {referenceLabel}: {selectedLead?.id ?? "nao informado"}</Typography>
          </Box>
          {assumeError && <Alert severity="error" sx={{ mb: 2 }}>{assumeError}</Alert>}
          {withinWindow ? <TextField autoFocus fullWidth multiline minRows={3} label="Primeira mensagem" value={firstMessage} onChange={(event) => setFirstMessage(event.target.value)} /> : <>
            <Alert severity="info" sx={{ mb: 2 }}>A ultima mensagem do contato tem mais de 24 horas. Escolha um template aprovado.</Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(280px, 0.9fr)" }, gap: 2 }}>
              <Box>
                {loadingTemplates ? <CircularProgress size={24} /> : <TextField select fullWidth label="Template" value={selectedTemplateId} onChange={(event) => { setSelectedTemplateId(event.target.value); setTemplateValues({}); }} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}><option value="">Selecione um template</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.name} ({template.language})</option>)}</TextField>}
                {selectedTemplate && templateVariables(selectedTemplate).map((key) => <TextField key={key} fullWidth sx={{ mt: 1.5 }} label={`Variavel ${key}`} value={templateValues[key] || ""} onChange={(event) => setTemplateValues((current) => ({ ...current, [key]: event.target.value }))} />)}
              </Box>
              <TemplateMessagePreview template={selectedTemplate} values={templateValues} />
            </Box>
          </>}
        </DialogContent>
        <DialogActions><Button onClick={resetAssume} disabled={submitting}>Cancelar</Button><Button variant="contained" onClick={assumeAndSend} disabled={submitting || (withinWindow ? !firstMessage.trim() : !selectedTemplate)}>{submitting ? "Assumindo..." : "Assumir e enviar mensagem"}</Button></DialogActions>
      </Dialog>
      <Dialog open={templateDialogOpen} onClose={() => !submitting && setTemplateDialogOpen(false)} fullWidth maxWidth="sm"><DialogTitle>Enviar template</DialogTitle><DialogContent><Typography color="text.secondary" sx={{ mb: 2 }}>Escolha um template aprovado para continuar o atendimento.</Typography>{loadingTemplates ? <CircularProgress size={24} /> : <TextField select fullWidth label="Template" value={selectedTemplateId} onChange={(event) => { setSelectedTemplateId(event.target.value); setTemplateValues({}); }} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}><option value="">Selecione um template</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.name} ({template.language})</option>)}</TextField>}{selectedTemplate && templateVariables(selectedTemplate).map((key) => <TextField key={key} fullWidth sx={{ mt: 1.5 }} label={`Variavel ${key}`} value={templateValues[key] || ""} onChange={(event) => setTemplateValues((current) => ({ ...current, [key]: event.target.value }))} />)}</DialogContent><DialogActions><Button onClick={() => setTemplateDialogOpen(false)} disabled={submitting}>Cancelar</Button><Button variant="contained" onClick={sendTemplate} disabled={submitting || !selectedTemplate}>Enviar template</Button></DialogActions></Dialog>
    </Box>
  );
}
